import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useStoreActions, useStoreState } from "easy-peasy";
import TargetPortfolioDialog from "./TargetPortfolioDialog";

// Target portfolio of a new record when several portfolios are active (#1409).
//
// choose(action) resolves the portfolio the record goes to, or null if the user cancels.
// With one active portfolio it resolves at once and nothing is shown, as before. With several
// it asks in TargetPortfolioDialog. The choice becomes the store's target portfolio
// (state.portfolio.portfolio), which the forms already read, and the active selection does
// not change. action: "purchase", "sale" or anything else for the general title.
//
// forRow(portfolio) makes an existing row's portfolio the target before its edit form opens,
// because the edit forms send the store's portfolio on update. Takes an id or { id }.
//
//   const { choose, forRow } = useTargetPortfolio()
//   const openPurchase = async () => { if (await choose("purchase")) setPurchaseModalOpen(true) }
//   const openEdit = (row) => { forRow(row.portfolio); setEditModalOpen(true) }

const TargetPortfolioContext = createContext(null);

export const TargetPortfolioProvider = ({ children }) => {
  const activePortfolios = useStoreState((state) => state.portfolio?.activePortfolios);
  const portfolios = useStoreState((state) => state.portfolio?.portfolios);
  const portfolio = useStoreState((state) => state.portfolio?.portfolio);
  const companies = useStoreState((state) => state.companies?.companies);
  const setPortfolio = useStoreActions((actions) => actions.portfolio.setPortfolio);

  // choose and forRow stay the same functions between renders, so callers can use them in
  // effect dependencies; they read the latest store values from here.
  const latest = useRef({});
  latest.current = { activePortfolios: activePortfolios || [], portfolios: portfolios || [], portfolio };

  const resolverRef = useRef(null);
  const openerRef = useRef(null);
  const [dialog, setDialog] = useState(null);
  const [returnFocus, setReturnFocus] = useState(null);

  const setTarget = useCallback((next) => {
    if (next && latest.current.portfolio?.id !== next.id) {
      setPortfolio(next);
    }
  }, [setPortfolio]);

  const choose = useCallback((action) => {
    const { activePortfolios: active, portfolio: target } = latest.current;
    if (active.length <= 1) {
      const only = active[0] || target || null;
      setTarget(only);
      return Promise.resolve(only);
    }
    return new Promise((resolve) => {
      if (resolverRef.current) {
        resolverRef.current(null);
      }
      resolverRef.current = resolve;
      openerRef.current = typeof document !== "undefined" ? document.activeElement : null;
      setDialog((previous) => ({ action, portfolios: active, key: (previous?.key || 0) + 1, isOpen: true }));
    });
  }, [setTarget]);

  const finish = useCallback((result) => {
    const resolve = resolverRef.current;
    resolverRef.current = null;
    setDialog((previous) => (previous ? { ...previous, isOpen: false } : previous));
    if (result) {
      setTarget(result);
    } else {
      setReturnFocus(openerRef.current);
    }
    openerRef.current = null;
    if (resolve) {
      resolve(result || null);
    }
  }, [setTarget]);

  // Back to the button that opened the dialog, after the dialog has closed.
  useEffect(() => {
    if (returnFocus && typeof returnFocus.focus === "function") {
      returnFocus.focus();
    }
    if (returnFocus) {
      setReturnFocus(null);
    }
  }, [returnFocus]);

  const forRow = useCallback((rowPortfolio) => {
    const id = rowPortfolio && typeof rowPortfolio === "object" ? rowPortfolio.id : Number(rowPortfolio);
    const found = latest.current.portfolios.find((p) => p.id === id) || null;
    setTarget(found);
    return found;
  }, [setTarget]);

  const value = useMemo(() => ({ choose, forRow }), [choose, forRow]);

  return (
    <TargetPortfolioContext.Provider value={value}>
      {children}
      {dialog && (
        <TargetPortfolioDialog
          key={dialog.key}
          isOpen={dialog.isOpen}
          action={dialog.action}
          portfolios={dialog.portfolios}
          companies={companies}
          onConfirm={(chosen) => finish(chosen)}
          onCancel={() => finish(null)}
        />
      )}
    </TargetPortfolioContext.Provider>
  );
};

export const useTargetPortfolio = () => {
  const context = useContext(TargetPortfolioContext);
  if (!context) {
    throw new Error("useTargetPortfolio must be used inside TargetPortfolioProvider");
  }
  return context;
};

export default useTargetPortfolio;
